import kebabCase from 'lodash/kebabCase';
import { COVER_PHOTO_HEIGHT_REBRAND, COVER_PHOTO_WIDTH_REBRAND, LOGO_HEIGHT_REBRAND, LOGO_WIDTH_REBRAND } from './constants/hiringEvents';

export const isObjectEmpty = (obj) => {
  if (obj === undefined || obj === null) return true;
  return Object.keys(obj).length === 0;
};

// lodash get alternative
export const get = (obj, path, defValue) => {
  if (!path) return undefined;
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
  const result = pathArray.reduce(
    (prevObj, key) => prevObj && prevObj[key],
    obj,
  );
  return result === undefined ? defValue : result;
};

export const validateImageDimensions = (file, coverPhoto) => new Promise(((resolve, reject) => {
  let IMAGE_WIDTH;
  let IMAGE_HEIGHT;

  if (coverPhoto) {
    IMAGE_WIDTH = COVER_PHOTO_WIDTH_REBRAND;
    IMAGE_HEIGHT = COVER_PHOTO_HEIGHT_REBRAND;
  } else {
    IMAGE_WIDTH = LOGO_WIDTH_REBRAND;
    IMAGE_HEIGHT = LOGO_HEIGHT_REBRAND;
  }

  const blob = file.originalFile;
  // Get an object URL for the local file
  const url = URL.createObjectURL(blob);

  // Create an image and load the object URL
  const img = new Image();
  img.src = url;

  // eslint-disable-next-line func-names
  img.onload = function () {
    URL.revokeObjectURL(url);

    if (coverPhoto) { // Cover picture checks
      if (this.naturalWidth < IMAGE_WIDTH) {
      // eslint-disable-next-line prefer-promise-reject-errors
        reject(`Image width should be ${IMAGE_WIDTH} pixels`);
      }
      if (this.naturalHeight < IMAGE_HEIGHT) {
      // eslint-disable-next-line prefer-promise-reject-errors
        reject(`Image height should be ${IMAGE_HEIGHT} pixels`);
      }
    } else if (this.naturalWidth < IMAGE_WIDTH || this.naturalHeight < IMAGE_HEIGHT) {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject(`Logo width and height should be at least ${IMAGE_WIDTH} pixels`);
    }

    // If we made it here then the file was approved
    resolve();
  };
}));


export const capitalize = string => (string ? string.charAt(0).toUpperCase() + string.slice(1).toLowerCase() : '');

export const isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length;

export const omit = (keys, obj) => Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

export const validateJobcaseContentUrl = (urlString) => {
  const base = 'www.jobcase.com';
  const isValidUrl = (urlString.includes(`${base}/ru/`) || urlString.includes(`${base}/conversations/`) || urlString.includes(`${base}/articles/`));
  return isValidUrl;
};

export const isValidHttpURL = (str) => {
  const pattern = new RegExp('^(https?:\\/\\/)?' // protocol
    + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
    + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
    + '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
  return !!pattern.test(str);
};

export const extractFeatureContentFromUrl = (data, section) => {
  const CONTENT_TYPE = {
    ru: 'ROUNDUP',
    conversations: 'CONVERSATION',
    articles: 'ARTICLE',
  };
  return data.map(({ value }, key) => {
    const validUrl = isValidHttpURL(value) ? `https://${value}` : value;
    const { pathname } = new URL(validUrl);
    const pathArray = pathname.split('/');
    const { length } = pathArray;
    const contentId = pathArray[length - 1] || '';
    const contentType = pathArray[length - 2] || '';
    return {
      content_id: contentId,
      display_section: section,
      display_type: 'FEATURED',
      content_type: CONTENT_TYPE[contentType] || CONTENT_TYPE.article,
      display_order: key,
    };
  });
};

export const joinUrlFeatureContentFromData = ({ content_id: id, content_type: type }) => {
  const CONTENT_TYPE = {
    ROUNDUP: 'ru',
    CONVERSATION: 'conversations',
    ARTICLE: 'articles',
  };
  return `https://www.jobcase.com/${CONTENT_TYPE[type]}/${id}`;
};

export const getImageName = (url) => {
  if (url) {
    const arr = url.split('/');
    return `${arr[arr.length - 1]}.jpg`;
  }
  return null;
};

export const stringChopping = (fullStr, strLen, separator = '...') => {
  if (!fullStr) return null;
  if (fullStr.length <= strLen) return fullStr;

  const charsToShow = strLen - separator.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return fullStr.substr(0, frontChars) + separator + fullStr.substr(fullStr.length - backChars);
};

export const toSentenceCase = str => (str ? str.charAt(0).toUpperCase() + str.toLowerCase().slice(1) : null);

export const toTitleCase = str => (str ? str.split(' ').map(word => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)).join(' ') : null);

export const isEventKeyALetter = (keyCode) => {
  const UPPERCASE_A_ASCII = 65;
  const UPPERCASE_Z_ASCII = 90;
  const LOWERCASE_A_ASCII = 97;
  const LOWERCASE_Z_ASCII = 122;

  return ((keyCode >= UPPERCASE_A_ASCII && keyCode <= UPPERCASE_Z_ASCII) || (keyCode >= LOWERCASE_A_ASCII && keyCode <= LOWERCASE_Z_ASCII));
};

export const getFormattedPhoneNumber = (phoneNumberString) => {
  const cleaned = (`${phoneNumberString}`).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return null;
};

export const extractDigitsFromFormattedPhoneNumber = (phoneNumber = '') => phoneNumber.replace(/\D+/g, '');

export const isFormattedPhoneNumberValid = (phoneNumber = '') => {
  const strippedPhoneNumber = phoneNumber == null ? '' : extractDigitsFromFormattedPhoneNumber(phoneNumber);
  const isValid = strippedPhoneNumber?.length === 10;
  return isValid;
};

export const getFullAddress = (streetAddress, city, state, zip, country) => {
  let fullAddress = { streetAddress, city, state };
  fullAddress = `${Object.values(fullAddress).filter(val => val).join(', ')} ${zip || ''}`.trim();
  // LUT-532: show the country on non-US addresses; the US display is unchanged.
  if (country && country !== 'US' && fullAddress) {
    fullAddress = `${fullAddress}, ${country}`;
  }
  return fullAddress;
};

export const cardContainsSubOrg = (card, subOrgId) => {
  const filterCard = card.sub_organizations.filter(subOrg => subOrg.sub_organization_id === subOrgId);
  return filterCard.length !== 0;
};

export const replaceEmptyValuesWithNull = (obj = {}) => {
  const sanitizedObj = {};
  Object.keys(obj).map((key) => {
    if (obj[key] !== '') {
      sanitizedObj[key] = obj[key];
    } else {
      sanitizedObj[key] = null;
    }
    return null;
  });
  return sanitizedObj;
};

export const isJson = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const getColumnWidth = width => (width >= 1920 ? 3 : 2);

export const splitAndJoin = (string, spliter) => string.split(/(?=[A-Z])/).join(spliter).toUpperCase();

export const stopPropagationWrapper = (event, func = () => {}) => {
  if (event?.stopPropagation) event.stopPropagation();
  func();
};

export const downloadCSVFile = (data, filename) => {
  const csvContent = `data:text/csv;charset=utf-8,${
    data.map(e => e.join(',')).join('\n')}`;
  const encodedUri = encodeURI(csvContent);
  const convertedUri = encodedUri.replace('#', '%23');
  const link = document.createElement('a');
  link.setAttribute('href', convertedUri);
  link.setAttribute('download', `${kebabCase(filename)}.csv`);
  document.body.appendChild(link);
  link.click();
};

export const getAlertsDependencyArray = queryObj => ([
  queryObj?.subOrgIds,
  queryObj?.jobGroupIds,
  queryObj?.jobIds,
  queryObj?.start,
  queryObj?.end,
  queryObj?.employer,
]);

export const getUniqueArrElementsForASpecificField = (arr = [], field) => {
  const set = new Set();
  const newArr = [];

  for (let i = 0; i < arr.length; i += 1) {
    const item = arr[i];

    if (!set.has(item[field])) {
      set.add(item[field]);
      newArr.push(item);
    }
  }

  return newArr;
};

export default {
  capitalize,
  isEmpty,
  isObjectEmpty,
  omit,
  toSentenceCase,
  validateJobcaseContentUrl,
  extractFeatureContentFromUrl,
  joinUrlFeatureContentFromData,
  getImageName,
  stringChopping,
  isEventKeyALetter,
  getFormattedPhoneNumber,
  extractDigitsFromFormattedPhoneNumber,
  isFormattedPhoneNumberValid,
  getFullAddress,
  cardContainsSubOrg,
  replaceEmptyValuesWithNull,
  isJson,
  splitAndJoin,
  stopPropagationWrapper,
  downloadCSVFile,
  getAlertsDependencyArray,
  getUniqueArrElementsForASpecificField,
};
